import * as React from "react";

/**
 * Undo and redo for an editor's value. Every change pushes the value before it, except a change
 * with the same `key` as the one before it within `COALESCE_MS` — a stepper clicked five times, a
 * name typed letter by letter — which replaces it, so one undo takes back the whole gesture.
 * Pure reducer for tests; `useHistory` wraps it.
 */

export const COALESCE_MS = 1000;
const LIMIT = 100;

export interface History<T> {
  past: T[];
  present: T;
  future: T[];
  lastKey: string | null;
  lastAt: number;
}

export type HistoryAction<T> =
  | { type: "update"; fn: (value: T) => T; key?: string; at: number }
  | { type: "undo" }
  | { type: "redo" }
  | { type: "reset"; value: T };

export function startHistory<T>(value: T): History<T> {
  return { past: [], present: value, future: [], lastKey: null, lastAt: 0 };
}

export function historyReducer<T>(h: History<T>, action: HistoryAction<T>): History<T> {
  switch (action.type) {
    case "update": {
      const next = action.fn(h.present);
      if (Object.is(next, h.present)) return h;
      const coalesce = action.key !== undefined && action.key === h.lastKey && action.at - h.lastAt < COALESCE_MS;
      return {
        past: coalesce ? h.past : [...h.past, h.present].slice(-LIMIT),
        present: next,
        future: [],
        lastKey: action.key ?? null,
        lastAt: action.at,
      };
    }
    case "undo": {
      if (!h.past.length) return h;
      const previous = h.past[h.past.length - 1] as T;
      return { past: h.past.slice(0, -1), present: previous, future: [h.present, ...h.future], lastKey: null, lastAt: 0 };
    }
    case "redo": {
      if (!h.future.length) return h;
      const [next, ...rest] = h.future as [T, ...T[]];
      return { past: [...h.past, h.present], present: next, future: rest, lastKey: null, lastAt: 0 };
    }
    case "reset":
      return startHistory(action.value);
  }
}

export interface Undoable<T> {
  value: T;
  /** A new value, or a function of the current one; `key` coalesces a gesture. */
  set: (next: T | ((value: T) => T), key?: string) => void;
  undo: () => void;
  redo: () => void;
  canUndo: boolean;
  canRedo: boolean;
  /** Start over from `value`, forgetting the history (a load, a save). */
  reset: (value: T) => void;
}

export function useHistory<T>(initial: T): Undoable<T> {
  const [h, dispatch] = React.useReducer(
    historyReducer as (h: History<T>, a: HistoryAction<T>) => History<T>,
    initial,
    startHistory,
  );
  const set = React.useCallback((next: T | ((value: T) => T), key?: string) => {
    const fn = typeof next === "function" ? (next as (value: T) => T) : () => next;
    dispatch({ type: "update", fn, key, at: Date.now() });
  }, []);
  const undo = React.useCallback(() => dispatch({ type: "undo" }), []);
  const redo = React.useCallback(() => dispatch({ type: "redo" }), []);
  const reset = React.useCallback((value: T) => dispatch({ type: "reset", value }), []);
  return { value: h.present, set, undo, redo, canUndo: h.past.length > 0, canRedo: h.future.length > 0, reset };
}
